import {
  DashboardActivityDetails,
  DashboardResolvedCommentDocument,
} from "@models";
import { Typography } from "@mui/material";
import type { ReactNode } from "react";
import { DashboardConnectedCard } from "./DashboardConnectedCard";
import { DashboardApprovalDocumentsList } from "./DashboardWaitingApprovalDocumentsList";
import { DashboardCommentThreadsDocumentList } from "./DashboardCommentThreadsDocumentList";
import { DashboardEmptyStateText } from "./DashboardEmptyStateText";
import { DashboardTaskRows } from "./DashboardTaskRows";

type DashboardActivityCardProps = {
  activity: DashboardActivityDetails;
  count?: number;
  orgId: string;
  selectedDeviceId?: string;
  fallbackDeviceId?: string;
  className?: string;
};

type DashboardActivitySectionProps = {
  title: string;
  testId: string;
  children: ReactNode;
  subtitleClassName?: string;
};

const getActivityCount = (activity: DashboardActivityDetails) =>
  activity.completedTasksLastWeek.length +
  activity.fullyApprovedDocumentsLastWeek.length +
  activity.resolvedCommentDocumentsLastWeek.length;

const DashboardResolvedCommentDocumentsList = ({
  documents,
  selectedDeviceId,
  fallbackDeviceId,
}: {
  documents: DashboardResolvedCommentDocument[];
  selectedDeviceId?: string;
  fallbackDeviceId?: string;
}) => (
  <DashboardCommentThreadsDocumentList
    documents={documents}
    selectedDeviceId={selectedDeviceId}
    fallbackDeviceId={fallbackDeviceId}
    emptyText="No resolved comments."
    threadStatusLabel="resolved"
    badgeColor="success"
    getThreadIds={(document) => document.resolvedThreadIds}
    rowTestIdPrefix="dashboard-resolved-comment-document-row"
    labelGroupTestIdPrefix="dashboard-resolved-comment-document-label-group"
    statusIconTestIdPrefix="dashboard-resolved-comment-document-status-icon"
    commentsButtonTestIdPrefix="dashboard-resolved-comment-document-comments-button"
    menuIdSuffix="resolved"
  />
);

const DashboardActivitySection = ({
  title,
  testId,
  children,
  subtitleClassName,
}: DashboardActivitySectionProps) => (
  <section className="flex min-h-0 flex-col" data-testid={testId}>
    <Typography
      variant="caption"
      color="text.secondary"
      className={["pl-[7px]", subtitleClassName].filter(Boolean).join(" ")}
    >
      {title}
    </Typography>
    {children}
  </section>
);

export const DashboardActivityCard = ({
  activity,
  count,
  orgId,
  selectedDeviceId,
  fallbackDeviceId,
  className,
}: DashboardActivityCardProps) => {
  const hasCompletedTasks = activity.completedTasksLastWeek.length > 0;
  const hasApprovedDocuments = activity.fullyApprovedDocumentsLastWeek.length > 0;
  const hasResolvedComments = activity.resolvedCommentDocumentsLastWeek.length > 0;
  const hasActivity =
    hasCompletedTasks || hasApprovedDocuments || hasResolvedComments;

  return (
    <DashboardConnectedCard
      rootTestId="dashboard-activity-connected-card"
      metricTestId="dashboard-digest-metric-card"
      caption="Team activity in the last 7 days"
      totalNumber={count ?? getActivityCount(activity)}
      metricClassName="border-emerald-200 bg-emerald-50"
      valueClassName="text-emerald-700"
      sectionLabel="Team activity in the last 7 days"
      sectionTestId="dashboard-activity-result-card"
      className={className}
    >
      {!hasActivity ? (
        <DashboardEmptyStateText>
          No activity in last 7 days.
        </DashboardEmptyStateText>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {hasCompletedTasks && (
            <DashboardActivitySection
              title="Tasks completed"
              testId="dashboard-activity-completed-tasks-section"
              subtitleClassName="pt-[7px]"
            >
              <DashboardTaskRows
                tasks={activity.completedTasksLastWeek}
                isLoading={false}
                emptyText="No tasks completed in last 7 days."
                listTestId="dashboard-completed-last-seven-days-task-list"
                scrollTestId="dashboard-completed-last-seven-days-tasks-scroll"
                selectedDeviceId={selectedDeviceId}
                fallbackDeviceId={fallbackDeviceId}
              />
            </DashboardActivitySection>
          )}
          {hasApprovedDocuments && (
            <DashboardActivitySection
              title="Documents fully approved"
              testId="dashboard-activity-approved-documents-section"
              subtitleClassName={!hasCompletedTasks ? "pt-[7px]" : undefined}
            >
              <DashboardApprovalDocumentsList
                documents={activity.fullyApprovedDocumentsLastWeek}
                isLoading={false}
                orgId={orgId}
                selectedDeviceId={selectedDeviceId}
                fallbackDeviceId={fallbackDeviceId}
                emptyText="No documents fully approved in last 7 days."
                loadingTestId="dashboard-approved-documents-loading"
                statusIconTestIdPrefix="dashboard-approved-document-status-icon"
              />
            </DashboardActivitySection>
          )}
          {hasResolvedComments && (
            <DashboardActivitySection
              title="Comments resolved"
              testId="dashboard-activity-resolved-comments-section"
              subtitleClassName={
                !hasCompletedTasks && !hasApprovedDocuments ? "pt-[7px]" : undefined
              }
            >
              <DashboardResolvedCommentDocumentsList
                documents={activity.resolvedCommentDocumentsLastWeek}
                selectedDeviceId={selectedDeviceId}
                fallbackDeviceId={fallbackDeviceId}
              />
            </DashboardActivitySection>
          )}
        </div>
      )}
    </DashboardConnectedCard>
  );
};
